import type {Post} from '~/models/Post';
import {first, map} from 'lodash-es';
import moment from 'moment/moment';
import {useHead} from '#app';

export function useGenerateSeoTagsForPost(post: Post) {
    const config = useRuntimeConfig().public;

    const basePath = config.appUrl;
    const route = useRoute();

    const author = !isEmpty(post?.authors) ? map(post?.authors ?? [], (author) => author.name).join(', ') : 'Sigmalive';

    let thumbnail: string = post.thumbnail?.seo_url ?? '';

    if (post.type === 'opinions') {
        thumbnail = first(post.authors)?.thumbnail?.original_url ?? '';
    }

    const articlePublishedTime = post.published_at ? moment(post.published_at).toISOString() : undefined;
    const articleModifiedTime = post.updated_at ? moment(post.updated_at).toISOString() : articlePublishedTime;

    useSeoMeta({
        title: post.title,
        description: post.title,
        ogType: 'website',
        ogUrl: `${basePath}${route.fullPath}`,
        ogTitle: post.title,
        ogDescription: post.title,
        ogImage: thumbnail ?? `${basePath}/images/meta_img.webp`,
        twitterCard: 'summary_large_image',
        twitterUrl: `${basePath}${route.fullPath}`,
        articlePublishedTime: articlePublishedTime,
        articleModifiedTime: articleModifiedTime,
        twitterTitle: post.title,
        twitterSite: config.twitterHandle,
        twitterDescription: post.title,
        twitterImage: thumbnail ?? `${basePath}/images/meta_img.webp`,
        ogImageWidth: '1200',
        ogImageHeight: '630',
        author: author,
    });

    const schemaData = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        'headline': post.title,
        'image': thumbnail ?? `${basePath}/images/meta_img.webp`,
        'datePublished': articlePublishedTime,
        'dateModified': articleModifiedTime,
        'author': {
            '@type': 'Person',
            'name': author,
        },
        'publisher': {
            '@type': 'Organization',
            'name': 'Sigmalive',
            'logo': {
                '@type': 'ImageObject',
                'url': 'https://www.sigmalive.com/apple-touch-icon.png',
            },
        },
        'mainEntityOfPage': `${basePath}${route.fullPath}`,
    };

    useHead({
        script: [
            {
                type: 'application/ld+json',
                innerHTML: JSON.stringify(schemaData),
            },
        ],
    });

}
